/**
 * StandupModal — "My Daily Standup" one-tap seed modal.
 *
 * 3 rows of title input. User can:
 *   • Use the mic to dictate each row.
 *   • Pick a priority per row.
 *   • Click "Seed Tasks" → POST /tasks/standup-seed → created in Task Manager.
 *   • Toggle "Don't ask me again" → PATCH /users/me/preferences.
 *
 * Opens from the "Daily Standup" button in TaskManager OR from the deep-links
 * fired by the 10:00 IST morning ('today') and 19:00 IST evening ('tomorrow')
 * cron reminders. When the deep-link carries ?for=YYYY-MM-DD the modal pivots
 * to a "Plan tomorrow" copy + posts `for_date` so the backend stamps the new
 * tasks with that due_date instead of today.
 */
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, X, Save, BellOff, Moon, Sun } from 'lucide-react';
import VoiceInput from './VoiceInput';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRIORITY_COLORS = {
  High: 'bg-red-900/30 text-red-300 border-red-700',
  Medium: 'bg-amber-900/30 text-amber-300 border-amber-700',
  Low: 'bg-zinc-800 text-zinc-300 border-zinc-700',
};

// Best-effort IST "today" so the "tomorrow?" comparison matches the operator's
// calendar regardless of server timezone. forDate from the URL is YYYY-MM-DD.
const istTodayTag = () => {
  const now = new Date();
  const ist = new Date(now.getTime() + (now.getTimezoneOffset() + 330) * 60 * 1000);
  return ist.toISOString().slice(0, 10);
};

const prettyDate = (tag) => {
  if (!tag) return '';
  try {
    const d = new Date(tag + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
  } catch { return tag; }
};

export default function StandupModal({ onClose, onSeeded, forDate }) {
  const today = istTodayTag();
  const isTomorrow = forDate && forDate !== today;
  const heading = isTomorrow
    ? `What are your top 3 priorities for tomorrow (${prettyDate(forDate)})?`
    : 'What are your top 3 priorities for today?';
  const title = isTomorrow ? '🌇 Plan Tomorrow' : 'My Daily Standup';

  const placeholders = useMemo(() => (
    isTomorrow
      ? [
          'e.g. Send pending PI to Anil Traders first thing',
          'e.g. Review tomorrow\'s production plan with Raju',
          'e.g. Confirm packaging dispatch slots',
        ]
      : [
          'e.g. Call Raju about packaging',
          'e.g. Approve GRN for turmeric lot',
          'e.g. Review tomorrow\'s production plan',
        ]
  ), [isTomorrow]);

  const [rows, setRows] = useState([
    { title: '', priority: 'High', module: 'Misc' },
    { title: '', priority: 'Medium', module: 'Misc' },
    { title: '', priority: 'Low', module: 'Misc' },
  ]);
  const [saving, setSaving] = useState(false);
  const [optingOut, setOptingOut] = useState(false);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    const items = rows.filter((r) => r.title.trim());
    if (items.length === 0) { toast.error('Enter at least one task'); return; }
    setSaving(true);
    try {
      const payload = { items };
      if (forDate) payload.for_date = forDate;
      const res = await axios.post(`${API}/tasks/standup-seed`, payload, { withCredentials: true });
      const n = res.data.count;
      const when = res.data.is_tomorrow ? 'tomorrow' : 'today';
      toast.success(`Seeded ${n} task${n === 1 ? '' : 's'} for ${when}`);
      onSeeded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to seed');
    } finally {
      setSaving(false);
    }
  };

  const optOut = async () => {
    if (!window.confirm('Stop the daily standup reminders (10 AM + 7 PM) for your account? You can re-enable from profile preferences.')) return;
    setOptingOut(true);
    try {
      await axios.patch(`${API}/users/me/preferences`, { daily_standup_enabled: false }, { withCredentials: true });
      toast.success('Daily standup turned off. See you when you need it again.');
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setOptingOut(false);
    }
  };

  const accentColor = isTomorrow ? 'indigo' : 'amber';
  const HeaderIcon = isTomorrow ? Moon : Sun;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="standup-modal" data-standup-mode={isTomorrow ? 'tomorrow' : 'today'}>
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-zinc-50 text-sm flex items-center gap-2">
              {isTomorrow ? <HeaderIcon size={14} className="text-indigo-300" /> : <Sparkles size={14} className="text-amber-400" />}
              {title}
            </h3>
            <p className="text-zinc-500 text-[11px] mt-0.5" data-testid="standup-heading">{heading}</p>
          </div>
          <button onClick={onClose} data-testid="standup-close-btn"><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
        </div>

        {isTomorrow && (
          <div className="mx-5 mt-4 px-3 py-2 rounded-lg bg-indigo-950/40 border border-indigo-800/60 text-indigo-200 text-[11px]">
            These tasks will be created with due-date <span className="font-semibold">{prettyDate(forDate)}</span> so they&apos;re waiting for you in tomorrow&apos;s standup.
          </div>
        )}

        <div className="p-5 space-y-3">
          {rows.map((r, i) => (
            <div key={`row-${i}`} className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3" data-testid={`standup-row-${i}`}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-zinc-400 text-[11px] font-semibold">Priority {i + 1}</label>
                <VoiceInput
                  onTranscript={(t) => setRow(i, { title: r.title ? `${r.title} ${t}` : t })}
                  context="standup"
                  size={12}
                  title="Speak this task"
                />
              </div>
              <input
                value={r.title}
                onChange={(e) => setRow(i, { title: e.target.value })}
                placeholder={placeholders[i]}
                className={`w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-${accentColor}-500`}
                data-testid={`standup-title-${i}`}
              />
              <div className="flex gap-1.5 mt-2">
                {['High', 'Medium', 'Low'].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setRow(i, { priority: p })}
                    className={`text-[10px] px-2 py-1 rounded border ${r.priority === p ? PRIORITY_COLORS[p] : 'bg-zinc-900 text-zinc-500 border-zinc-800 hover:text-zinc-300'}`}
                    data-testid={`standup-priority-${i}-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={optOut}
              disabled={optingOut}
              className="text-[11px] text-zinc-500 hover:text-red-400 flex items-center gap-1 transition-colors disabled:opacity-50"
              data-testid="standup-opt-out-btn"
            >
              <BellOff size={11} /> Stop daily reminders
            </button>
            <div className="flex-1" />
            <button type="button" onClick={onClose} className="py-2 px-4 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm" data-testid="standup-skip-btn">
              Skip
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className={`py-2 px-5 rounded-xl ${isTomorrow ? 'bg-indigo-500 hover:bg-indigo-400 text-white' : 'bg-amber-500 hover:bg-amber-400 text-black'} font-bold text-sm flex items-center gap-1.5 disabled:opacity-50`}
              data-testid="standup-save-btn"
            >
              <Save size={13} /> {saving ? 'Seeding…' : (isTomorrow ? 'Plan Tomorrow' : 'Seed Tasks')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
